import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success("Welcome back!");
      navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAFC]">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white">
        <Link to="/" className="flex items-center gap-2">
          <Logo textClassName="text-white" tagline />
        </Link>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight">Every bill you pay<br /><span className="text-emerald-400">earns you rewards.</span></h2>
          <p className="text-slate-400 mt-4 max-w-sm">Log in to pay electricity, generators, internet, mobile, insurance and more — and track your wallet & reward points.</p>
        </div>
        <p className="text-slate-500 text-sm">© 2026 HARMONEYS · Enjoy The Difference</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <Logo />
          </Link>
          <h1 className="font-display text-3xl font-bold text-slate-900">Welcome back</h1>
          <p className="text-slate-500 mt-2">Log in to your HARMONEYS account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="font-semibold">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" placeholder="you@example.com" className="mt-1.5 bg-slate-50 py-5" />
            </div>
            <div>
              <Label htmlFor="password" className="font-semibold">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" placeholder="••••••••" className="mt-1.5 bg-slate-50 py-5" />
            </div>
            {error && <p className="text-sm text-red-600 font-medium" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold py-6">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log in"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            No account? <Link to="/register" className="text-emerald-600 font-semibold hover:underline" data-testid="go-register">Sign up free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
